import * as Sentry from '@sentry/react';
import { Box, BoxProps, Divider, Flex, MenuItemProps } from '@televet/kibble-ui';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { uniqBy } from 'lodash';
import useLinkPatients from 'pages/Conversations/hooks/useLinkPatients';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IAvatarGroupSelectOption } from 'shared/components/Avatars/AvatarGroupSelect';
import { PetAvatarProps } from 'shared/components/Avatars/PetAvatar';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useAddChannelMember from 'shared/hooks/useAddChannelMember';
import useClinicUser from 'shared/hooks/useClinicUser';
import useIsMobile from 'shared/hooks/useIsMobile';
import useRemoveChannelMember from 'shared/hooks/useRemoveChannelMember';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import {
  ChannelViewChannelFragment,
  ChannelViewClinicPetParentFragment,
  ClinicPetParent,
  GetChannelPetsQuery,
  useGetChannelPetsQuery,
} from 'shared/types/graphql';
import { ParticipantUpdate, ParticipantUpdateType } from '../enums/ParticipantUpdate';
import ParticipantSection from './ParticipantSection';
import PetParentParticipantItem from './PetParentParticipantItem';
import PetParticipantItem from './PetParticipantItem';

type ParticipantsTabProps = {
  petParentOptions: IAvatarGroupSelectOption[];
  handleLoadClientDropdownOptions: () => void;
  channelPetParents: ChannelViewClinicPetParentFragment[];
  isPetParentInAnotherConversation: (petParent: ClinicPetParent) => boolean;
  clinicPetParentIds: string[];
  onClose: () => void;
  channel?: ChannelViewChannelFragment;
};

type IAvatarGroupSelectOptionClinicPetParent = IAvatarGroupSelectOption['clinicPetParent'] & {
  displayName?: string;
  pets?: NonNullable<GetChannelPetsQuery>['findManyClinicPetParent'][0]['pets'];
};

type IAvatarGroupSelectOptionWithPets = IAvatarGroupSelectOption & {
  isAdding?: boolean;
  clinicPetParent?: IAvatarGroupSelectOptionClinicPetParent;
};

const ParticipantsTab = ({
  petParentOptions,
  handleLoadClientDropdownOptions,
  channelPetParents,
  isPetParentInAnotherConversation,
  clinicPetParentIds,
  onClose,
  channel,
}: ParticipantsTabProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const [isLoading, setIsLoading] = useState(false);
  const { viewAddClientOrPatient } = useSidePanelSearch();
  const { isMobile } = useIsMobile();
  const channelId = channel?.id || '';
  const linkedPets = useMemo(() => channel?.pets || [], [channel?.pets]);
  const { trackEvent } = useTracking();
  const { data: pets } = useGetChannelPetsQuery({
    variables: {
      where: { id: { in: clinicPetParentIds } },
      petsWhere: { clinicId: { equals: currentClinicId || '' }, isDeleted: { equals: false } },
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });
  const { addOrRemovePatient } = useLinkPatients(channel?.id);
  const toast = useToast();
  const [addChannelMember] = useAddChannelMember();
  const [removeChannelMember] = useRemoveChannelMember();

  // Pet parents
  const [temporarySelectedPetParentIds, setTemporarySelectedPetParentIds] = useState<string[]>(
    channelPetParents.map((p) => p.id),
  );

  const [temporaryPetParentOptions, setTemporaryPetParentOptions] = useState<IAvatarGroupSelectOptionWithPets[]>([]);

  useEffect(() => {
    handleLoadClientDropdownOptions();
    // We need to load the client dropdown options when the component mounts only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTemporaryPetParentOptions((prev) => {
      const mergedMap = new Map<string, IAvatarGroupSelectOptionWithPets>();

      // Add previous options first
      prev.forEach((option) => {
        mergedMap.set(option.value.id, option);
      });

      // Merge with petParentOptions, preserving isAdding if it already exists
      petParentOptions.forEach((option) => {
        const key = option.value.id;
        if (mergedMap.has(key)) {
          const existing = mergedMap.get(key);
          mergedMap.set(key, { ...option, isAdding: existing?.isAdding });
        } else {
          mergedMap.set(key, option);
        }
      });

      return Array.from(mergedMap.values());
    });
  }, [petParentOptions]);

  useEffect(() => {
    if (pets?.findManyClinicPetParent) {
      setTemporaryPetParentOptions((prevOptions) => {
        const parentById = new Map(pets.findManyClinicPetParent.map((parent) => [parent.id, parent]));

        return prevOptions.map((option) => {
          const parent = parentById.get(option.value.id);
          if (!parent || !option.clinicPetParent) return option;

          // Find only the deceased or inactive pets not already in the option
          const newPets = parent.pets.filter((pet) => {
            const isDeceasedOrInactive = pet.computedIsDeceased || !pet.isActive;
            const alreadyAdded = option?.clinicPetParent?.pets?.some((existing) => existing.id === pet.id);
            return isDeceasedOrInactive && !alreadyAdded;
          });

          // If nothing to add, keep the original option
          if (newPets.length === 0) return option;

          // Otherwise return a fresh object with pets appended
          return {
            ...option,
            clinicPetParent: {
              ...option.clinicPetParent,
              pets: [...(option?.clinicPetParent?.pets || []), ...newPets],
            },
          };
        });
      });
    }
  }, [pets, petParentOptions]);

  const onAddClient = useCallback((): void => {
    viewAddClientOrPatient({
      conversationId: channelId,
      isPanelZIndexIncreased: true,
      onClientAdd: {
        callback: (petParent) => {
          if (!temporaryPetParentOptions.find((p) => p.value.id === petParent.id)) {
            setTemporaryPetParentOptions((prev) => [
              ...prev,
              {
                isAdding: true,
                text: `${petParent.firstName} ${petParent.lastName}`,
                value: petParent,
                clinicPetParent: petParent,
              },
            ]);
            setTemporarySelectedPetParentIds((prev) => [...prev, petParent.id]);
          }
        },
      },
    });
    trackEvent(ConversationEvents.ChannelAddNewPatient, { source: 'Details Modal - Participants Tab' });
  }, [channelId, temporaryPetParentOptions, viewAddClientOrPatient, trackEvent]);

  const selectedPetParents = useMemo(() => {
    return temporaryPetParentOptions
      .filter((option) => {
        return temporarySelectedPetParentIds.includes(option.value.id);
      })
      .map((option) => option.clinicPetParent) as IAvatarGroupSelectOptionClinicPetParent[];
  }, [temporaryPetParentOptions, temporarySelectedPetParentIds]);

  const { petParentMenuItems } = useMemo(() => {
    const petParentMenuItems: (MenuItemProps & {
      padding?: BoxProps['padding'];
      key?: string;
    })[] = [];

    if (!isMobile) {
      petParentMenuItems.push({
        padding: 0,
        children: (
          <Button variant="ghost" size="sm" iconName="plus" onClick={onAddClient} pl={5} w="100%" justifyContent="left">
            Add New Client
          </Button>
        ),
      });
    }

    for (const petParent of temporaryPetParentOptions) {
      const {
        value: { firstName, lastName, id },
        clinicPetParent,
      } = petParent;

      const name = clinicPetParent?.displayName ?? [firstName, lastName].filter((n) => !!n).join(' ');
      const isPartOfAnotherConversation = isPetParentInAnotherConversation(petParent.value as ClinicPetParent);
      const isSelected = !!temporarySelectedPetParentIds.find((petParentId) => petParentId === id);

      const isDisabled =
        !isSelected && (isPartOfAnotherConversation || clinicPetParent?.isDeleted || !clinicPetParent?.isActive);

      let disabledLabel = '';
      if (isPartOfAnotherConversation) {
        disabledLabel = 'This client is already a member of an active conversation';
      } else if (clinicPetParent?.isDeleted) {
        disabledLabel = 'This client is deleted';
      } else if (!clinicPetParent?.isActive) {
        disabledLabel = 'This client is inactive';
      }

      petParentMenuItems.push({
        label: !!disabledLabel ? (
          <Tooltip titleCaseLabel={false} placement="right" label={disabledLabel}>
            <Box w="100%">{name}</Box>
          </Tooltip>
        ) : (
          name
        ),
        value: id,
        isSelected,
        groupLabel: 'Related Clients',
        isDisabled: isDisabled,
        key: id,
      });
    }

    return { petParentMenuItems };
  }, [
    isMobile,
    onAddClient,
    temporaryPetParentOptions,
    isPetParentInAnotherConversation,
    temporarySelectedPetParentIds,
  ]);

  // End of pet parents

  // Pets
  const [temporarySelectedPetIds, setTemporarySelectedPetIds] = useState<string[]>((linkedPets || []).map((p) => p.id));

  const availablePets = useMemo(() => {
    const allPets = [
      ...(pets?.findManyClinicPetParent || []).map((petParent) => petParent.pets).flat(),
      ...temporaryPetParentOptions
        .filter(({ value: { id } }) => temporarySelectedPetParentIds.includes(id))
        .map(({ clinicPetParent }) => clinicPetParent?.pets || [])
        .flat(),
    ];

    return uniqBy(allPets, 'id');
  }, [pets?.findManyClinicPetParent, temporaryPetParentOptions, temporarySelectedPetParentIds]);

  const { petMenuItems, petAvatars } = useMemo(() => {
    const petMenuItems: MenuItemProps[] = [];
    const petAvatars: (Pick<PetAvatarProps, 'speciesType' | 'isDeceased' | 'id' | 'name' | 'isActive'> & {
      isOrphan?: boolean;
    })[] = [];

    const finalPetParentList = temporaryPetParentOptions
      .filter((p) => temporarySelectedPetParentIds.includes(p.value.id))
      .map((petParent) => petParent?.clinicPetParent?.pets || [])
      .flat();

    for (const pet of availablePets) {
      const { name, id, computedIsDeceased, speciesType, isActive } = pet;
      const isSelected = !!temporarySelectedPetIds.find((id) => id === pet.id);
      const isOrphan = !finalPetParentList.find((p) => p.id === pet.id);

      petMenuItems.push({
        label: name,
        value: id,
        isSelected,
        groupLabel: !isActive || computedIsDeceased ? 'Inactive Patients' : 'Active Patients',
      });

      if (isSelected) {
        petAvatars.push({
          name,
          id,
          speciesType,
          isDeceased: computedIsDeceased,
          isActive: !!isActive,
          isOrphan,
        });
      }
    }

    // Active patients should be at the top of the list
    petMenuItems.sort((a, b) => (a.groupLabel === 'Active Patients' ? -1 : b.groupLabel === 'Active Patients' ? 1 : 0));

    return {
      petMenuItems,
      petAvatars,
    };
  }, [availablePets, temporaryPetParentOptions, temporarySelectedPetIds, temporarySelectedPetParentIds]);
  // End of pets

  const updateParticipants = useCallback(
    async (channelId: string, petParentIds: ParticipantUpdate[], petIds: ParticipantUpdate[]): Promise<boolean> => {
      const promises: Promise<unknown>[] = [];

      for (const { id, type } of petParentIds) {
        if (type === 'adding') {
          promises.push(addChannelMember({ variables: { channelId, clinicPetParentId: id } }));
        } else {
          const channelMemberId = channel?.channelMembers.find(({ clinicPetParent }) => clinicPetParent?.id === id)?.id;
          if (channelMemberId) {
            promises.push(removeChannelMember({ variables: { channelMemberId } }));
          }
        }
      }

      if (petIds.length) {
        const petIdsToAdd: string[] = [];
        const petIdsToRemove: string[] = [];

        for (const { id, type } of petIds) {
          if (type === 'adding') {
            petIdsToAdd.push(id);
          } else {
            petIdsToRemove.push(id);
          }
        }
        promises.push(addOrRemovePatient(petIdsToAdd, petIdsToRemove));
      }

      if (!promises.length) {
        return true;
      }

      try {
        await Promise.all(promises);
        toast({
          description: 'Participants updated successfully',
          duration: 10000,
        });
        return true;
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
        toast({
          status: 'error',
          description: 'An error occurred in updating participants',
          duration: 10000,
        });
        return false;
      }
    },
    [addChannelMember, addOrRemovePatient, channel?.channelMembers, removeChannelMember, toast],
  );

  const onSaveParticipants = useCallback(async (): Promise<void> => {
    const currentPetParentIds = channelPetParents.map((p) => p.id);
    const petParentIdsToAdd = temporarySelectedPetParentIds.filter((id) => !currentPetParentIds.includes(id));
    const petParentIdsToRemove = currentPetParentIds.filter((id) => !temporarySelectedPetParentIds.includes(id));

    // Compare the current pet parents with the stored ones
    const participantUpdates: ParticipantUpdate[] = [
      ...petParentIdsToAdd.map((id) => ({ id, type: ParticipantUpdateType.Adding })),
      ...petParentIdsToRemove.map((id) => ({ id, type: ParticipantUpdateType.Removing })),
    ];

    // Pets are a little different, they are dependent on the pet parents
    const petUpdates: ParticipantUpdate[] = [
      ...linkedPets
        .filter((p) => !temporarySelectedPetIds.includes(p.id))
        .map((pet) => ({ id: pet.id, type: ParticipantUpdateType.Removing })),
    ];

    const finalPetsAvailableList = temporaryPetParentOptions
      .filter((p) => temporarySelectedPetParentIds.includes(p.value.id))
      .map((petParent) => petParent?.clinicPetParent?.pets || [])
      .flat();

    const finalPetList = availablePets.filter((p) => temporarySelectedPetIds.includes(p.id));

    for (const pet of finalPetList) {
      if (!finalPetsAvailableList.find((p) => p.id === pet.id)) {
        if (linkedPets.find((p) => p.id === pet.id)) {
          // This pet is orphaned (belongs to a pet parent that was removed), we need to remove it
          petUpdates.push({ id: pet.id, type: ParticipantUpdateType.Removing });
        }
      } else {
        // New pet added
        if (!linkedPets.find((p) => p.id === pet.id)) {
          petUpdates.push({ id: pet.id, type: ParticipantUpdateType.Adding });
        }
      }
    }

    setIsLoading(true);
    try {
      await updateParticipants(channelId, participantUpdates, petUpdates);
      onClose();
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  }, [
    availablePets,
    channelId,
    channelPetParents,
    linkedPets,
    onClose,
    temporaryPetParentOptions,
    temporarySelectedPetIds,
    temporarySelectedPetParentIds,
    updateParticipants,
  ]);

  return (
    <Flex height="100%" direction="column">
      <Box maxHeight="260px" overflow="auto" px={4}>
        <ParticipantSection
          title="Clients"
          participants={selectedPetParents}
          renderParticipant={(participant): JSX.Element => (
            <PetParentParticipantItem key={participant.id} clinicPetParent={participant} />
          )}
          addButtonText="Add Client"
          menuItems={petParentMenuItems}
          onSelect={(selectedItem): void => {
            setTemporarySelectedPetParentIds((prev) => {
              const updated = [...prev];
              const petParentId = selectedItem.value || '';
              if (selectedItem.isSelected) {
                updated.push(petParentId);
              } else {
                const index = updated.indexOf(petParentId);
                if (index > -1) {
                  updated.splice(index, 1);
                }
              }
              return updated;
            });
          }}
          onOpen={handleLoadClientDropdownOptions}
          emptyMessage="Please add at least one Client."
        />

        <Divider borderBottomWidth="1px" borderBottomColor="border.default" my={5} />

        <ParticipantSection
          title="Patients"
          participants={petAvatars}
          renderParticipant={(participant): JSX.Element => (
            <PetParticipantItem key={participant.id} {...participant} name={participant.name || ''} />
          )}
          addButtonText="Link Patient"
          menuItems={petMenuItems}
          onSelect={(selectedItem): void => {
            setTemporarySelectedPetIds((prev) => {
              const updated = [...prev];
              const petId = selectedItem.value || '';
              if (selectedItem.isSelected) {
                updated.push(petId);
              } else {
                const index = updated.indexOf(petId);
                if (index > -1) {
                  updated.splice(index, 1);
                }
              }
              return updated;
            });
          }}
          pb={3}
        />
      </Box>

      <Flex
        className="ParticipantsTab__Footer"
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        justifyContent="space-between"
        py={3}
        px={4}
        shadow="mdR"
      >
        <Button variant="tertiary" onClick={onClose} className="ParticipantsTab__Footer-CancelButton">
          Cancel
        </Button>
        <Button
          className="ParticipantsTab__Footer-SaveButton"
          mr={2}
          isLoading={isLoading}
          isDisabled={isLoading}
          disabled={temporarySelectedPetParentIds.length === 0}
          onClick={onSaveParticipants}
        >
          Save
        </Button>
      </Flex>
    </Flex>
  );
};

export default ParticipantsTab;
